import { cn } from '@lib/cn';
import { shortDate } from '@lib/time';
import {
    Badge,
    Card,
    CardHeader,
    IconArrowLeft,
    ErrorState,
    KeyValue,
    LoadingRows,
    PageHeader,
} from '@shared/ui';
import { Link, Outlet, useLocation, useParams } from 'react-router';

import { useClientTarget } from '../hooks/use-client-target';
import { useClient } from '../hooks/use-clients';
import { describeTarget, targetParams } from '../schemas/target';

import { ProductionBand } from './EnvironmentScope';
import { isTabActive } from './tab-active';

import type { ClientDetail } from '../schemas/client';
import type { ReactNode } from 'react';

/**
 * One client, in one environment — everything its tabs share.
 *
 * ## What it reports, and how confidently
 *
 * Whether anyone can sign in comes from identity-api's directory, not from
 * anything this app remembers. It briefly did remember — for want of that read
 * — and the gap showed on the General tab: a client with a working owner
 * rendered as "not onboarded through the Backoffice", beside an offer to add
 * one.
 *
 * The read answers three ways, and the third is kept: people, nobody, or the
 * directory could not be asked. Only the second offers to add an owner.
 *
 * ## Why the tabs re-ask for the client instead of being handed it
 *
 * `<Outlet context>` would carry it down already-narrowed, and was rejected:
 * every other screen in this feature — `ClientsPage`, `ProvisionOwnerPage` —
 * opens by calling `useClientTarget()` and its own query, and a tab that only
 * works when mounted under one particular parent is a different kind of
 * component than the ones next to it. The tabs call `useClient` with the same
 * key, so TanStack answers them from the cache this layout already filled;
 * there is no second request, only a second reader.
 *
 * What that costs is one narrowing per tab — the layout has already returned
 * for pending, error and absent, so a tab reaching its own `!data` branch is
 * unreachable in practice and renders nothing rather than duplicating states
 * this file owns.
 *
 * ## Why `/owner` is not a tab
 *
 * Adding the owner is a task with an end: it is entered from a warning, it
 * writes, and it leads somewhere else. Tabs are places you can be; putting a
 * one-way form in the strip would offer it as one more view of the client and
 * invite the click on the ~all clients that already have one.
 */
export function ClientDetailLayout() {
    const { tenantId = '' } = useParams();
    const targetState = useClientTarget();
    const { target, isSlotMissing } = targetState;
    const client = useClient(target, tenantId);

    const search = new URLSearchParams(targetParams(target)).toString();

    /**
     * Where you came from, above the title — the same shape `FlagDetailPage`
     * uses, and for the same reason it is not a `Button` on the right.
     *
     * A ghost button in the header's action slot sits where the things you can
     * *do* to this client sit, and reads as one of them. Going back is not an
     * action on the client; it is the way out, and the way out belongs at the
     * top-left where a reader's eye starts.
     */
    const backToClients = (
        <Link
            to={{ pathname: '/clients', search }}
            className="mb-3 inline-flex items-center gap-1.5 text-[13px] font-medium text-content-muted transition-colors hover:text-accent-icon"
        >
            <IconArrowLeft className="size-4" /> Clients
        </Link>
    );

    /**
     * Wraps every outcome, including the ones that end early.
     *
     * The production band used to sit below these returns, so on `prd` the one
     * screen that declares itself declared nothing while it loaded or when it
     * failed — and `EnvironmentScope` calls that band "always visible, never
     * dismissible". An error on production is exactly when knowing which
     * environment you are in matters.
     */
    const frame = (children: ReactNode) => (
        <>
            {backToClients}
            {target.env === 'prd' && (
                <div className="mb-5">
                    <ProductionBand />
                </div>
            )}
            {children}
        </>
    );

    // `eph` with no slot chosen. `useClient` is DISABLED in that state — there
    // is no deployment to ask — and a disabled query stays `isPending` forever,
    // so without this branch the screen loads until the tab is closed. Reached
    // by a trimmed link or a hand-edited URL, which is also how somebody would
    // arrive at the list's equivalent branch.
    if (isSlotMissing) {
        return frame(
            <>
                <PageHeader eyebrow="Tenants" title="No slot chosen" />
                <Card>
                    <div className="p-5 text-[13.5px] text-content-subtle">
                        <p>
                            This link names an ephemeral target without saying which slot. Each slot
                            is its own deployment, with its own clients — pick one from the list.
                        </p>
                    </div>
                </Card>
            </>,
        );
    }

    if (client.isPending) return frame(<LoadingRows label="Loading the client…" />);
    if (client.isError) {
        return frame(<ErrorState error={client.error} onRetry={() => void client.refetch()} />);
    }
    if (!client.data) {
        // Not "no such client" — a client id is unique within ONE environment,
        // so the honest sentence names the one we looked in.
        return frame(
            <>
                <PageHeader eyebrow="Tenants" title="Not in this environment" />
                <Card>
                    <div className="p-5 text-[13.5px] text-content-subtle">
                        <p>
                            No client with that id in <b>{describeTarget(target)}</b>. It may exist
                            in another environment — clients are not shared between them.
                        </p>
                    </div>
                </Card>
            </>,
        );
    }

    const data = client.data;

    // No switcher here, unlike the list. A tenant id belongs to the environment
    // it was created in: pointing this screen at another one does not show the
    // same client elsewhere, it asks a backend that has never heard of it.
    // Which environment this is already reads in the line under the title —
    // what would be new is the ability to get it wrong. Production still
    // declares itself, from `frame` above, on every outcome.
    return frame(
        <>
            <PageHeader
                eyebrow="Tenants · Client"
                title={data.displayName}
                description={<ClientIdentityLine client={data} where={describeTarget(target)} />}
            />

            <TabStrip tenantId={data.tenantId} search={search} />

            {/* Same two-column shape as the flag detail: the tab's content on
                the left, the facts that identify the thing being read in a
                320px rail on the right. The rail belongs to the layout rather
                than to the General tab because "which client is this" does not
                stop being the question when you open Billing. */}
            <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
                {/* `min-w-0` is load-bearing: a grid track defaults to
                    `min-width: auto`, so a wide table inside a tab would size
                    this column to its own content and shove the rail off the
                    right edge. Tables scroll; the page must not. */}
                <div className="min-w-0">
                    <Outlet />
                </div>

                <ClientFacts client={data} />
            </div>
        </>,
    );
}

/**
 * The identifiers, in the rail.
 *
 * They moved out of a full-width "Identity" card on the General tab, where they
 * were the first thing read on the only tab that had anything else to say. They
 * are reference, not content: you look one up, you do not work through them.
 *
 * `Type` is deliberately here as well as in the line under the title. The line
 * is what keeps a sandbox client from being mistaken for a real one at a
 * glance; this row is where someone checking the record expects to find it.
 */
function ClientFacts({ client }: { client: ClientDetail }) {
    return (
        <div className="flex flex-col gap-4">
            <Card>
                <CardHeader title="Client" />
                <div className="px-5 py-2">
                    <KeyValue label="Tenant ID">
                        <span className="font-mono text-[12px]">{client.tenantId}</span>
                    </KeyValue>
                    {/* The address a visitor uses — a URL segment in the
                        membership and ticketing apps, which is why it is
                        permanent and why it is shown even when unset. */}
                    <KeyValue label="Public address">
                        {client.slug ? (
                            <span className="font-mono text-[12px]">{client.slug}</span>
                        ) : (
                            <span className="text-content-faint">None</span>
                        )}
                    </KeyValue>
                    <KeyValue label="Type">
                        {client.isSandbox === true ? (
                            <Badge tone="warning" dot>
                                Sandbox
                            </Badge>
                        ) : (
                            <Badge tone="active" dot>
                                {client.status}
                            </Badge>
                        )}
                    </KeyValue>
                    <KeyValue label="Created">{shortDate(client.createdAt)}</KeyValue>
                </div>
            </Card>
        </div>
    );
}

/**
 * Where this client lives, what it is addressed as, and whether it is real.
 *
 * The status badge sits under the title rather than in the Identity card
 * because it is the fact that decides whether the rest of the screen is worth
 * reading, and it has to stay legible from whichever tab is open — a sandbox
 * client answering a billing question is not the same client. Identity keeps
 * the type as a row too, where it belongs among the fields.
 */
function ClientIdentityLine({ client, where }: { client: ClientDetail; where: string }) {
    return (
        <span className="flex flex-wrap items-center gap-2.5">
            <span>{where}</span>
            {client.slug && (
                <span className="font-mono text-[12.5px] text-content-subtle">{client.slug}</span>
            )}
            {client.isSandbox === true ? (
                <Badge tone="warning" dot>
                    Sandbox
                </Badge>
            ) : (
                <Badge tone="active" dot>
                    {client.status}
                </Badge>
            )}
        </span>
    );
}

/**
 * The four views of a client, as links that keep the target.
 *
 * Every href carries the `?env=&slot=` the operator arrived with — the same
 * `targetParams` string `ClientsPage` appends to its rows, and for the same
 * reason. A tab that dropped it would fall back to the deployed environment on
 * click, so an operator reading a production client would land on a dev one
 * wearing the same tenant id, silently. That is precisely the failure the
 * whole target design exists to prevent, and a navigation control inside the
 * scoped screens is the easiest place to reintroduce it.
 *
 * Active state is computed from `useLocation` rather than delegated to
 * `NavLink`: `Sidebar` already does it this way, and `end`-style matching would
 * still need a special case for the index tab, whose path is a prefix of the
 * others.
 */
function TabStrip({ tenantId, search }: { tenantId: string; search: string }) {
    const { pathname } = useLocation();
    const base = `/clients/${tenantId}`;

    const tabs = [
        { to: base, label: 'General' },
        { to: `${base}/products`, label: 'Product settings' },
        /*
         * Beside Product settings rather than inside it. The two are different
         * altitudes of the same data — an entitlement materialises into the
         * override rows this tab lists — and one is authored by Product while
         * the other is an engineering switch most of whose keys are meant to be
         * deleted at GA. Nesting them under one heading would say they are the
         * same kind of thing; adjacency says they are related, which is true.
         */
        { to: `${base}/flags`, label: 'Feature flags' },
        { to: `${base}/billing`, label: 'Billing' },
    ];

    return (
        <nav aria-label="Client sections" className="mb-5 flex gap-1 border-b border-line">
            {tabs.map((tab) => {
                const active = isTabActive(pathname, tab.to, tab.to === base);
                return (
                    <Link
                        key={tab.to}
                        to={{ pathname: tab.to, search }}
                        aria-current={active ? 'page' : undefined}
                        className={cn(
                            '-mb-px border-b-2 px-3.5 py-2.5 text-[13.5px] font-medium transition-colors',
                            active
                                ? 'border-brand text-accent-text'
                                : 'border-transparent text-content-muted hover:text-content',
                        )}
                    >
                        {tab.label}
                    </Link>
                );
            })}
        </nav>
    );
}
